import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
} from "@/api/client";

interface CalendarEventFilters {
  month?: number; // 0-indexed
  year?: number;
  studyId?: string;
  status?: string;
}

export function useCalendarEvents(filters?: CalendarEventFilters) {
  const { month, year, studyId, status } = filters ?? {};

  // Compute epoch ms range for the visible month (padded ±7 days for grid overflow)
  let startAfter: number | undefined;
  let startBefore: number | undefined;

  if (month !== undefined && year !== undefined) {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    startAfter = first.getTime() - 7 * 24 * 60 * 60 * 1000;
    startBefore = last.getTime() + 8 * 24 * 60 * 60 * 1000;
  }

  return useQuery({
    queryKey: ["calendarEvents", { month, year, studyId, status }],
    queryFn: () =>
      listCalendarEvents({
        studyId,
        status,
        startAfter,
        startBefore,
        limit: 200,
      }),
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateCalendarEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCalendarEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
    },
  });
}

export function useUpdateCalendarEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCalendarEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
    },
  });
}

export function useDeleteCalendarEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCalendarEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
    },
  });
}
